<template>
  <span
    ref="elRef"
    class="icon"
    :data-icon-resolved="resolvedIcon"
    :data-icon="icon"
    :class="{ 'icon--as-image': asImage }"
  />
</template>

<script setup lang="ts">
// based on https://github.com/antfu/vitesse/blob/1dc4d210405d63d195a944fc5bd97deebd17cb75/src/components/Icon.vue
import { watch, ref, unref, onMounted, nextTick, computed } from 'vue';
import useIcons from '../stores/useIcons';
import Iconify from '@iconify/iconify';
import bugAlert from '../lib/bugAlert';

const props = defineProps<{ icon: string; asImage?: boolean }>();

const { resolveIcon, themeIcons } = useIcons();
const elRef = ref<HTMLSpanElement>();
const resolvedIcon = computed(() => resolveIcon(props.icon));
const iconError = ref('');
const hasIconError = computed(() => iconError.value !== '');

const update = async () => {
  const el = unref(elRef);
  const icon = resolvedIcon.value;

  if (!el) return;
  if (!icon) return;

  await nextTick();

  // bundled icons
  let svgString = Iconify.renderSVG(icon, {})?.outerHTML;

  if (!svgString) {
    // not bundled, try to fetch from iconify
    svgString = await fetch(
      `https://api.iconify.design/${icon.replace(':', '/')}.svg`
    )
      .then(r => r.text())
      .catch(() => {
        iconError.value = `Failed to fetch icon: ${icon}`;
        console.error(iconError.value);
        return '';
      });

    if (svgString && !svgString?.startsWith('<svg')) {
      iconError.value = `No icon found for ${icon}`;
      console.error(iconError.value);
    }
  }

  // alert in dev mode
  if (import.meta.env.DEV && hasIconError.value) {
    bugAlert(iconError.value);
  }

  if (hasIconError.value) {
    const li = el.closest('ul > li');
    if (li) {
      // bullet-point for icons used in list items (fluent-mdl2:radio-bullet)
      el.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="2048" height="2048" viewBox="0 0 2048 2048"><path fill="currentColor" d="M1024 640q79 0 149 30t122 82t83 123t30 149q0 80-30 149t-82 122t-123 83t-149 30q-80 0-149-30t-122-82t-83-122t-30-150q0-79 30-149t82-122t122-83t150-30"/></svg>`;
    } else {
      // info icon otherwise (ci:info)
      el.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><g fill="currentColor"><path d="M11 10.98a1 1 0 1 1 2 0v6a1 1 0 1 1-2 0zm1-4.929a1 1 0 1 0 0 2a1 1 0 0 0 0-2"/><path fill-rule="evenodd" d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10s10-4.477 10-10S17.523 2 12 2M4 12a8 8 0 1 0 16 0a8 8 0 0 0-16 0" clip-rule="evenodd"/></g></svg>`;
    }
  } else if (svgString) {
    const span = document.createElement('span');
    span.innerHTML = svgString;
    const svg = span.children[0] as SVGElement;

    if (props.asImage) {
      svg.removeAttribute('width');
      svg.removeAttribute('height');
    }

    el.textContent = '';
    el.appendChild(svg);
  } else {
    const span = document.createElement('span');
    span.className = 'iconify';
    span.dataset.icon = icon;
    el.textContent = '';
    el.appendChild(span);
  }
};

watch([resolvedIcon, themeIcons], update, { flush: 'post' });

onMounted(update);
</script>

<style scoped>
.icon:not(.icon--as-image) {
  display: inline-block;
  height: 1em;
  width: 1em;
  vertical-align: middle;
}

.icon :deep(svg) {
  height: 1em;
  width: 1em;
}

.icon--as-image {
  @apply w-full;
}
.icon--as-image :deep(svg) {
  @apply h-auto w-full;
}
</style>
